import { useState, useMemo, useEffect } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useLanguage } from "../context/LanguageContext";
import { localStrings } from "../i18n/homeStrings";

// Declared here rather than written into the markup: an escape sequence is
// decoded in a JavaScript string but printed character for character in JSX.
const RANGE_SEPARATOR = "\u2013";

// How many years a single page of the year view holds. Twelve fits the same
// three rows the month view uses, so the popover does not change height
// between the two.
const YEARS_PER_PAGE = 12;

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** "2026-08-27" \u2192 Date, built locally so it does not shift by a timezone. */
function parseValue(value) {
  if (!value) return null;
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return isNaN(date) ? null : date;
}

/** Date to "2026-08-27", without going through UTC. */
function formatValue(date) {
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${m}-${d}`;
}

/** The first year of the page a given year falls on. */
function pageStart(year) {
  return Math.floor(year / YEARS_PER_PAGE) * YEARS_PER_PAGE;
}

/**
 * A date field with the same month grid as the home page calendar.
 *
 * The browser's own date input was doing this job, and it looked like the
 * browser's own date input: a different shape, a different typeface and a
 * different set of controls from everything around it. This is the grid
 * already used on the home page, minus the two things that were specific to
 * it \u2014 week numbers and holidays, which say nothing useful about when a vial
 * was calibrated.
 *
 * Three views rather than one. The month grid alone meant one click per month
 * to reach anything far back, which is fine for last week and useless for a
 * date two years ago. The heading is now a button: it opens the months of the
 * year, and the year in that view opens a page of years. Coming back down
 * narrows the cursor rather than choosing a date, so nothing is selected until
 * a day is clicked.
 *
 * Value in and out is "YYYY-MM-DD", the same as the input it replaces, so
 * callers do not change.
 */
export default function DatePicker({ value, onChange, testId, placeholder }) {
  const { language } = useLanguage();
  const s = localStrings(language);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState("days");
  const selected = useMemo(() => parseValue(value), [value]);
  const [cursor, setCursor] = useState(() => selected || new Date());
  const today = new Date();

  // Reopening on a date chosen earlier should land on that month, not on
  // whatever month was last browsed to, and always on the day grid rather than
  // wherever the last visit was left.
  useEffect(() => {
    if (!open) return;
    setView("days");
    if (selected) setCursor(selected);
  }, [open, selected]);

  const weeks = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const first = new Date(year, month, 1);

    const start = new Date(first);
    start.setDate(start.getDate() - ((first.getDay() + 6) % 7)); // Monday-first

    const rows = [];
    const walker = new Date(start);
    for (let row = 0; row < 6; row++) {
      const days = [];
      for (let col = 0; col < 7; col++) {
        days.push(new Date(walker));
        walker.setDate(walker.getDate() + 1);
      }
      rows.push(days);
      if (days[0].getMonth() !== month && row > 0) break;
    }
    return rows;
  }, [cursor]);

  const dayLabels = useMemo(() => {
    const base = new Date(2026, 0, 5); // a Monday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base);
      d.setDate(d.getDate() + i);
      return d.toLocaleDateString(language, { weekday: "narrow" });
    });
  }, [language]);

  const monthLabels = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        new Date(2026, i, 1).toLocaleDateString(language, { month: "short" })
      ),
    [language]
  );

  const monthLabel = cursor.toLocaleDateString(language, {
    month: "long",
    year: "numeric",
  });

  const yearPageStart = pageStart(cursor.getFullYear());

  const shown = selected
    ? selected.toLocaleDateString(language, {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : null;

  const choose = (date) => {
    onChange(formatValue(date));
    setOpen(false);
  };

  // The arrows mean a different step in each view, so they are wired once here
  // rather than three times in the markup.
  const step = (direction) => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    if (view === "days") setCursor(new Date(y, m + direction, 1));
    else if (view === "months") setCursor(new Date(y + direction, m, 1));
    else setCursor(new Date(y + direction * YEARS_PER_PAGE, m, 1));
  };

  const headingText =
    view === "days"
      ? monthLabel
      : view === "months"
      ? String(cursor.getFullYear())
      : `${yearPageStart} ${RANGE_SEPARATOR} ${yearPageStart + YEARS_PER_PAGE - 1}`;

  const headingAction =
    view === "days" ? "months" : view === "months" ? "years" : null;

  const cellTone = (isCurrent, isChosen) => {
    if (isChosen) return "bg-indigo-600 text-white font-bold hover:bg-indigo-600";
    if (isCurrent) return "text-indigo-700 font-semibold hover:bg-indigo-50";
    return "text-slate-700 hover:bg-indigo-50";
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`flex h-10 w-full items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm transition-colors hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-600 ${
            shown ? "text-slate-800" : "text-slate-400"
          }`}
        >
          <CalendarIcon className="w-4 h-4 text-slate-400 shrink-0" />
          <span className="truncate capitalize">{shown || placeholder}</span>
        </button>
      </PopoverTrigger>

      {/* Fixed width so the popover does not resize as the views change. */}
      <PopoverContent align="start" className="w-auto p-3">
        <div className="w-56">
          <div className="flex items-center justify-between mb-2 gap-2">
            {headingAction ? (
              <button
                type="button"
                onClick={() => setView(headingAction)}
                data-testid={testId ? `${testId}-heading` : undefined}
                className="min-w-0 truncate rounded px-1.5 py-0.5 -ml-1.5 text-xs font-semibold text-slate-700 capitalize hover:bg-slate-100 hover:text-indigo-700 transition-colors"
              >
                {headingText}
              </button>
            ) : (
              <span className="min-w-0 truncate px-1.5 py-0.5 -ml-1.5 text-xs font-semibold text-slate-700">
                {headingText}
              </span>
            )}
            <div className="flex gap-0.5 shrink-0">
              <button
                type="button"
                onClick={() => step(-1)}
                aria-label={s("feed.previous")}
                className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => step(1)}
                aria-label={s("feed.next")}
                className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {view === "days" && (
            <div className="grid grid-cols-7 gap-y-0.5 text-center">
              {dayLabels.map((d, i) => (
                <span key={i} className="text-[9px] font-semibold uppercase text-slate-400 pb-1">
                  {d}
                </span>
              ))}

              {weeks.flat().map((d, i) => {
                const inMonth = d.getMonth() === cursor.getMonth();
                const isToday = isSameDay(d, today);
                const isSelected = selected && isSameDay(d, selected);

                let tone = "text-slate-300 hover:bg-slate-50";
                if (inMonth) tone = "text-slate-700 hover:bg-indigo-50";
                if (isToday && !isSelected) tone = "text-indigo-700 font-semibold hover:bg-indigo-50";
                if (isSelected) tone = "bg-indigo-600 text-white font-bold hover:bg-indigo-600";

                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => choose(d)}
                    className={`w-8 h-8 text-xs rounded transition-colors ${tone}`}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </div>
          )}

          {view === "months" && (
            <div className="grid grid-cols-3 gap-1" data-testid={testId ? `${testId}-months` : undefined}>
              {monthLabels.map((label, i) => {
                const isCurrent =
                  today.getFullYear() === cursor.getFullYear() && today.getMonth() === i;
                const isChosen =
                  selected &&
                  selected.getFullYear() === cursor.getFullYear() &&
                  selected.getMonth() === i;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      setCursor(new Date(cursor.getFullYear(), i, 1));
                      setView("days");
                    }}
                    className={`h-9 text-xs rounded capitalize transition-colors ${cellTone(isCurrent, isChosen)}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {view === "years" && (
            <div className="grid grid-cols-4 gap-1" data-testid={testId ? `${testId}-years` : undefined}>
              {Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearPageStart + i).map((year) => {
                const isCurrent = today.getFullYear() === year;
                const isChosen = selected && selected.getFullYear() === year;
                return (
                  <button
                    key={year}
                    type="button"
                    onClick={() => {
                      setCursor(new Date(year, cursor.getMonth(), 1));
                      setView("months");
                    }}
                    className={`h-9 text-xs rounded transition-colors ${cellTone(isCurrent, isChosen)}`}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-2 pt-2 border-t border-slate-100 flex justify-between">
            <button
              type="button"
              onClick={() => { onChange(""); setOpen(false); }}
              className="text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
            >
              {s("date.clear")}
            </button>
            <button
              type="button"
              onClick={() => choose(new Date())}
              className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              {s("date.today")}
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
